import { motion, AnimatePresence } from 'framer-motion'
import { estimatePassphraseEntropy } from '../../lib/passphraseEntropy'
import { getContent, PassphraseLocale } from '../../lib/passphraseContent'
import { tokenizeAndClassify } from '../../lib/classifyToken'

interface PassphraseExamplesProps {
  locale: PassphraseLocale;
}

/**
 * PassphraseExamples - Minimal monochrome examples display
 *
 * "Simplicity is the ultimate sophistication." — Leonardo da Vinci
 */

// Monochrome token styling
const TOKEN_TYPE_COLORS: Record<string, string> = {
  WORD: 'border border-white/20 text-white/80',
  EMOJI: 'border border-white/30 text-white',
  FILLER: 'border border-white/10 text-white/30 italic',
  DIGIT: 'border border-white/20 text-white/70',
  CHAR: 'border border-white/20 text-white/70',
}

export default function PassphraseExamples({ locale }: PassphraseExamplesProps) {
  const content = getContent(locale);

  const getEntropyStrength = (bits: number) => {
    if (bits < 48) return { label: 'Weak', color: 'bg-white/20', textColor: 'text-white/40' }
    if (bits < 70) return { label: 'OK', color: 'bg-white/40', textColor: 'text-white/60' }
    return { label: 'Strong', color: 'bg-white', textColor: 'text-white' }
  }

  return (
    <div className="space-y-6 py-4">
      <AnimatePresence>
        <motion.div
          initial="hidden"
          animate="visible"
          variants={{
            hidden: { opacity: 0 },
            visible: {
              opacity: 1,
              transition: {
                staggerChildren: 0.1,
              },
            },
          }}
          className="space-y-6"
        >
          {content.examples.items.map((example, index) => {
            const tokens = tokenizeAndClassify(example.phrase, locale as 'en' | 'es');
            const entropy = estimatePassphraseEntropy(example.phrase, locale as 'en' | 'es');
            const bits = entropy.bits;
            const strength = getEntropyStrength(bits);

            return (
              <motion.div
                key={index}
                variants={{
                  hidden: { opacity: 0, y: 20 },
                  visible: { opacity: 1, y: 0 },
                }}
                transition={{ duration: 0.4, ease: 'easeOut' }}
                className="p-6 border border-white/10 space-y-4"
              >
                {/* Example Passphrase */}
                <div className="space-y-2">
                  <p className="text-xs text-white/30 uppercase tracking-widest">
                    Example
                  </p>
                  <p className="text-white/80 font-mono text-sm break-all">
                    {example.phrase}
                  </p>
                </div>

                {/* Token Chips */}
                <div className="flex flex-wrap gap-2">
                  {tokens.map((token, tokenIndex) => (
                    <span
                      key={tokenIndex}
                      className={`px-2 py-1 text-xs font-light ${TOKEN_TYPE_COLORS[token.type] || 'border border-white/10 text-white/60'}`}
                    >
                      {token.value}
                    </span>
                  ))}
                </div>

                {/* Entropy */}
                <div className="flex items-center gap-4">
                  <div className="flex items-center gap-2">
                    <div className="w-16 h-px bg-white/10 overflow-hidden">
                      <div
                        className={`h-full ${strength.color}`}
                        style={{ width: `${Math.min((bits / 100) * 100, 100)}%` }}
                      />
                    </div>
                    <span className="text-xs text-white/40">
                      {bits.toFixed(0)} bits
                    </span>
                  </div>
                  <span className={`text-xs ${strength.textColor}`}>
                    {strength.label}
                  </span>
                </div>

                {/* Explanation */}
                <p className="text-xs text-white/40 font-light">
                  {example.explanation}
                </p>

                {/* Warning */}
                <p className="text-[10px] text-white/20 uppercase tracking-widest border-t border-white/5 pt-3">
                  Do not reuse this exact phrase
                </p>
              </motion.div>
            );
          })}
        </motion.div>
      </AnimatePresence>
    </div>
  );
}
